import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { LazyGlitchTipService } from './lazy-glitchtip.service';
import { createServiceLogger } from '../utils/logger.util';
import { NetworkService } from './network.service';

/**
 * Interceptor to validate HTTP response content types and detect malformed responses
 * This helps catch cases where HTML error pages are returned instead of expected JSON
 */
@Injectable()
export class ResponseValidationInterceptor implements HttpInterceptor {
  private logger = createServiceLogger('ResponseValidationInterceptor');

  constructor(
    private glitchTipService: LazyGlitchTipService,
    private networkService: NetworkService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.validateResponse(event, req);
        }
      }),
      catchError((error) => this.handleResponseError(error, req)),
    );
  }

  private validateResponse(response: HttpResponse<any>, request: HttpRequest<any>): void {
    // Only validate API endpoints that should return JSON
    if (!this.isApiEndpoint(request.url)) {
      return;
    }

    // Check if response has headers with get method
    if (!response.headers || typeof response.headers.get !== 'function') {
      this.logger.warn('Invalid response headers detected', {
        operation: 'validate_response_headers',
        metadata: {
          url: request.url,
          hasHeaders: !!response.headers,
          headersType: typeof response.headers,
        },
      });
      return;
    }

    const contentType = response.headers.get('content-type') || '';
    const responseBody = response.body;

    // Check if we received HTML instead of JSON
    if (this.isHtmlResponse(contentType, responseBody)) {
      const error = new Error(`HTML response received from ${request.url} - expected JSON`);

      this.glitchTipService.captureApiError(error, {
        operation: 'response_validation',
        endpoint: request.url,
        method: request.method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        statusCode: response.status,
        additionalData: {
          contentType,
          responsePreview: typeof responseBody === 'string' ? responseBody.substring(0, 200) : 'Non-string response',
          expectedJson: true,
          url: request.url,
        },
      });

      this.logger.warn('HTML response detected for API endpoint', {
        operation: 'html_response_detected',
        metadata: {
          url: request.url,
          contentType,
          status: response.status,
        },
      });
    }

    // Check for empty responses when data is expected
    if (response.status === 200 && !responseBody && request.method === 'GET') {
      this.logger.warn('Empty response body for GET request', {
        operation: 'empty_response_detected',
        metadata: { url: request.url },
      });
    }
  }

  private handleResponseError(error: any, request: HttpRequest<any>): Observable<never> {
    // Enhanced error handling for response format issues
    if (error instanceof HttpErrorResponse) {
      const responseText = error.error;
      const isOffline = this.networkService.isOffline();
      // Check for abort errors - HttpErrorResponse.name is always "HttpErrorResponse",
      // so we check the underlying error or message instead
      const isAbort =
        error.error?.name === 'AbortError' ||
        error.message?.includes('aborted') ||
        error.message?.includes('AbortError');
      const isTimeout = error.status === 0 && !isOffline && !isAbort;
      const isNetworkError =
        error.status === 0 || error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError');

      // Skip reporting offline/abort errors - these are expected
      if (isOffline || isAbort) {
        return throwError(() => error);
      }

      // Check if error response contains HTML
      if (typeof responseText === 'string' && this.isHtmlContent(responseText)) {
        const enhancedError = new Error(`Server returned HTML error page instead of JSON for ${request.url}`);

        this.glitchTipService.captureApiError(enhancedError, {
          operation: 'response_error_validation',
          endpoint: request.url,
          method: request.method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
          statusCode: error.status,
          networkError: isNetworkError,
          timeout: isTimeout,
          additionalData: {
            originalError: error.message,
            responsePreview: responseText.substring(0, 200),
            isHtmlError: true,
            url: request.url,
          },
        });

        return throwError(() => enhancedError);
      }

      // Capture other HTTP errors only if not routine network issues
      if (!isNetworkError && error.status >= 500) {
        this.glitchTipService.captureApiError(error as any, {
          operation: 'http_error',
          endpoint: request.url,
          method: request.method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
          statusCode: error.status,
          timeout: isTimeout,
          additionalData: {
            errorMessage: error.message,
          },
        });
      }
    }

    return throwError(() => error);
  }

  private isApiEndpoint(url: string): boolean {
    return (
      url.includes('/api/') ||
      url.includes('ping')
    );
  }

  private isHtmlResponse(contentType: string, responseBody: any): boolean {
    // Check content type
    if (contentType.includes('text/html') || contentType.includes('application/xhtml')) {
      return true;
    }

    // Check response body content
    if (typeof responseBody === 'string') {
      return this.isHtmlContent(responseBody);
    }

    return false;
  }

  private isHtmlContent(content: string): boolean {
    if (!content || typeof content !== 'string') {
      return false;
    }

    const htmlIndicators = [
      '<!DOCTYPE html',
      '<html',
      '<head>',
      '<body>',
      '<title>',
      '<meta name=',
      '<script',
      '<link rel=',
    ];

    const lowerContent = content.toLowerCase().trim();
    return htmlIndicators.some((indicator) => lowerContent.includes(indicator));
  }
}
